/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { unfixUpStarEdge } from "./edge.js";
export class EdgeCache {
    #factory;
    #map = new Map();
    constructor(factory) {
        this.#factory = factory;
    }
    rebuild(graph) {
        // Initialize the edge map from the graph. This is only done once, and all
        // following updates are performed incrementally.
        const mainGraphEdges = new Map(graph.edges.map((edge) => [edge, this.#factory(edge, "")]));
        this.#map.set("", mainGraphEdges);
        Object.entries(graph.graphs || {}).forEach(([graphId, graph]) => {
            const subGraphEdges = new Map(graph.edges.map((edge) => [edge, this.#factory(edge, graphId)]));
            this.#map.set(graphId, subGraphEdges);
        });
    }
    get(edge, graphId) {
        return this.#map.get(graphId)?.get(edge);
    }
    getOrCreate(edge, graphId) {
        let result = this.get(edge, graphId);
        if (result) {
            return result;
        }
        result = this.#factory(edge, graphId);
        this.add(edge, graphId);
        return result;
    }
    add(edge, graphId) {
        console.assert(!this.#map.get(graphId)?.has(edge), "Edge already exists when adding.");
        let graphEdges = this.#map.get(graphId);
        if (!graphEdges) {
            graphEdges = new Map();
            this.#map.set(graphId, graphEdges);
        }
        graphEdges.set(edge, this.#factory(edge, graphId));
    }
    remove(edge, graphId) {
        console.assert(this.#map.get(graphId)?.has(edge), "Edge not found when removing.");
        const inspectableEdge = this.#map.get(graphId)?.get(edge);
        this.#map.get(graphId)?.delete(edge);
        inspectableEdge?.setDeleted();
    }
    has(edge, graphId) {
        return !!this.#map.get(graphId)?.has(edge);
    }
    hasByValue(edge, graphId) {
        edge = unfixUpStarEdge(edge);
        const edges = this.edges(graphId);
        return !!edges.find((e) => {
            return (e.from.descriptor.id === edge.from &&
                e.to.descriptor.id === edge.to &&
                e.out === edge.out &&
                e.in === edge.in);
        });
    }
    edges(graphId) {
        return Array.from(this.#map.get(graphId)?.values() || []);
    }
    addSubgraphEdges(subgraph, graphId) {
        subgraph.edges.map((edge) => {
            this.add(edge, graphId);
        });
    }
    removeSubgraphEdges(graphId) {
        this.#map.get(graphId)?.forEach((inspectableEdge) => {
            inspectableEdge.setDeleted();
        });
        this.#map.delete(graphId);
    }
}
//# sourceMappingURL=edge-cache.js.map